import { Extension } from "@tiptap/core";
import {
    Fragment,
    Slice,
    type Mark,
    type Node as PMNode,
} from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { AttrStep, ReplaceAroundStep } from "@tiptap/pm/transform";
import { isHistoryTransaction } from "@tiptap/pm/history";
import { nodeTag } from "./groupFormat";

/** Inline styles the theme owns: a line gets them from its style (H1…P), never
 *  from pasted HTML or a style it used to have. Colour stays (it's the author's). */
const THEME_OWNED = ["fontFamily", "fontWeight", "fontSize"];

function cleanTextStyle(mark: Mark): Mark | null {
    const attrs: Record<string, unknown> = { ...mark.attrs };
    for (const key of THEME_OWNED) if (key in attrs) attrs[key] = null;
    return Object.values(attrs).some(Boolean) ? mark.type.create(attrs) : null;
}

function cleanMarks(marks: readonly Mark[]): Mark[] {
    return marks.flatMap((m) => {
        if (m.type.name !== "textStyle") return [m];
        const cleaned = cleanTextStyle(m);
        return cleaned ? [cleaned] : [];
    });
}

function cleanFragment(fragment: Fragment): Fragment {
    const nodes: PMNode[] = [];
    fragment.forEach((node) => {
        const copy = node.isText
            ? node
            : node.copy(cleanFragment(node.content));
        nodes.push(copy.mark(cleanMarks(node.marks)));
    });
    return Fragment.from(nodes);
}

function cleanLine(tr: Transaction, pos: number) {
    const line = tr.doc.nodeAt(pos);
    if (!line?.isTextblock) return;
    line.forEach((child, offset) => {
        const mark = child.marks.find((m) => m.type.name === "textStyle");
        if (!mark || !THEME_OWNED.some((k) => mark.attrs[k])) return;
        const start = pos + 1 + offset;
        const end = start + child.nodeSize;
        tr.removeMark(start, end, mark);
        const cleaned = cleanTextStyle(mark);
        if (cleaned) tr.addMark(start, end, cleaned);
    });
}

/** Lines (positions in `tr.doc`) whose style (H1…H6, P, Label, Small) `tr` changed. */
function restyledLines(tr: Transaction): number[] {
    const lines: number[] = [];
    tr.steps.forEach((step, i) => {
        const pos =
            step instanceof ReplaceAroundStep
                ? step.from
                : step instanceof AttrStep
                  ? step.pos
                  : null;
        if (pos === null) return;
        const before = nodeTag(tr.docs[i].nodeAt(pos));
        const after = nodeTag((tr.docs[i + 1] ?? tr.doc).nodeAt(pos));
        if (before && after && before !== after)
            lines.push(tr.mapping.slice(i + 1).map(pos));
    });
    return lines;
}

/** Pasted text, and any line whose style changes (toolbar, shortcut, `## `,
 *  group mirror), loses its own font, weight and size. Marks stay. */
export const FontCleanup = Extension.create({
    name: "fontCleanup",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("fontCleanup"),
                props: {
                    transformPasted: (slice) =>
                        new Slice(
                            cleanFragment(slice.content),
                            slice.openStart,
                            slice.openEnd
                        ),
                },
                appendTransaction: (transactions, _oldState, state) => {
                    let lines: number[] = [];
                    for (const tr of transactions) {
                        lines = lines.map((pos) => tr.mapping.map(pos));
                        // Undo/redo brings back the fonts the change removed.
                        if (!isHistoryTransaction(tr))
                            lines.push(...restyledLines(tr));
                    }
                    if (lines.length === 0) return null;
                    const tr = state.tr;
                    for (const pos of lines) cleanLine(tr, pos);
                    return tr.docChanged ? tr : null;
                },
            }),
        ];
    },
});
